import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, Crown, ShieldCheck, DeviceMobile, QrCode } from "@phosphor-icons/react";
import { QrScanner, formatMac } from "@/components/QrScanner";
import { QRCodeSVG } from "qrcode.react";
import { ActivationSteps } from "@/components/ActivationSteps";
import { usePlans, useSiteUrl } from "@/hooks/usePlans";
import { api, errMsg, formatPrice, planNote, siteLabel } from "@/lib/api";

const Spinner = ({ white }) => <span className={`inline-block h-5 w-5 animate-spin rounded-full border-2 border-t-transparent ${white ? "border-white" : "border-brand"}`} />;

const statusText = (d) => {
  if (d.status === "active") {
    if (d.plan === "lifetime") return "Aktivan • Doživotno";
    if (d.expires_at) return `Aktivan • ističe ${new Date(d.expires_at * 1000).toLocaleDateString("sr-RS")}`;
    return "Aktivan";
  }
  if (d.status === "trial") return `Probni period • još ${d.days_left ?? 0} dana`;
  if (d.status === "blocked") return "Uređaj je blokiran";
  return "Probni period je istekao";
};

const PlanPicker = ({ device, plans, loading, paying, onPay }) => {
  if (device.status === "active" && device.plan === "lifetime")
    return <p className="py-3 text-center font-semibold text-ok" data-testid="already-lifetime">Ovaj uređaj je već aktiviran doživotno. 🎉</p>;
  return (
    <>
      <p className="mt-1 text-base font-semibold">{device.status === "active" ? "Produžite pretplatu" : device.status === "expired" ? "Obnovite pristup" : "Izaberite paket"}</p>
      {device.status === "active" && <p className="text-[11px] leading-4 text-muted2">Novo vreme se dodaje na vaš postojeći datum isteka.</p>}
      {device.status === "expired" && <p className="text-[11px] leading-4 text-muted2">Vaš pristup je istekao — obnovite ga godišnje ili pređite na doživotno.</p>}
      {loading && <Spinner />}
      {plans.map((p) => (
        <button key={p.id} onClick={() => onPay(p.id)} disabled={!!paying} data-testid={`web-plan-${p.id}`}
          className={`flex items-center gap-3 rounded-xl border bg-surface2 p-4 text-left transition-transform hover:-translate-y-0.5 disabled:opacity-60 ${p.days === null ? "border-brand" : "border-line"}`}>
          <Crown size={22} weight="fill" className={p.days === null ? "text-brand" : "text-txt2"} />
          <div className="flex-1">
            <p className="text-sm font-semibold">{device.status === "active" && p.id === "yearly" ? "Produži za godinu dana" : p.name}</p>
            <p className="mt-0.5 text-[11px] text-muted2">{planNote(p)}</p>
          </div>
          {paying === p.id ? <Spinner /> : <span className="text-xl font-bold text-brand">{formatPrice(p)}</span>}
        </button>
      ))}
      <p className="text-[11px] leading-4 text-muted2">Plaćanje se obrađuje bezbedno preko Stripe-a. Uređaj se aktivira automatski nakon uplate.</p>
    </>
  );
};

const PricePreview = ({ plans }) => (
  <div className="mt-2 flex flex-col gap-2" data-testid="price-preview">
    <p className="text-base font-semibold">Cene</p>
    <div className="grid grid-cols-2 gap-2">
      {plans.map((p) => (
        <button key={p.id} type="button" onClick={() => document.getElementById("dk")?.focus()} data-testid={`price-preview-${p.id}`}
          className={`flex flex-col gap-1 rounded-xl border bg-surface2 p-4 text-left transition-transform hover:-translate-y-0.5 ${p.days === null ? "border-brand" : "border-line"}`}>
          <Crown size={20} weight="fill" className={p.days === null ? "text-brand" : "text-txt2"} />
          <span className="text-sm font-semibold">{p.name}</span>
          <span className="text-2xl font-bold text-brand">{formatPrice(p)}</span>
          <span className="text-[11px] text-muted2">{planNote(p)}</span>
        </button>
      ))}
    </div>
    <p className="text-[11px] leading-4 text-muted2">Unesite Device Key i MAC (ili skenirajte QR kod sa TV-a) i kliknite „Proveri uređaj” da biste platili.</p>
  </div>
);

const DownloadCard = () => {
  const url = `${useSiteUrl()}/landing`;
  return (
    <div className="mt-4 flex w-full max-w-[460px] flex-col items-center gap-2 rounded-[20px] border border-line bg-surface2 p-4" data-testid="download-card">
      <div className="flex items-center gap-2"><DeviceMobile size={22} weight="fill" className="text-brand" /><span className="text-base font-bold">Nemate aplikaciju?</span></div>
      <p className="text-center text-xs leading-[18px] text-txt2">Skenirajte QR kod telefonom da preuzmete BalkanHD Play.</p>
      <div className="mt-1 rounded-xl bg-white p-3"><QRCodeSVG value={url} size={160} /></div>
      <a href={url} target="_blank" rel="noreferrer" data-testid="download-link" className="mt-1 text-sm font-semibold text-brand">{siteLabel(url)}</a>
    </div>
  );
};

export default function ActivateWeb() {
  const [params] = useSearchParams();
  const [deviceKey, setDeviceKey] = useState((params.get("key") || "").toUpperCase());
  const [mac, setMac] = useState(formatMac(params.get("mac") || ""));
  const [device, setDevice] = useState(null);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [paying, setPaying] = useState(null);
  const [scanning, setScanning] = useState(false);
  const { plans, loading } = usePlans();
  const creds = () => ({ device_key: deviceKey.trim(), mac: mac.trim() });

  const lookup = async (device_key, macAddr) => {
    setError(""); setDevice(null);
    if (!device_key || !macAddr) return setError("Unesite Device Key i MAC adresu.");
    setChecking(true);
    try { setDevice((await api.post("/public/device-lookup", { device_key, mac: macAddr })).data); }
    catch (err) { setError(errMsg(err, "Provera nije uspela.")); }
    finally { setChecking(false); }
  };
  const check = (e) => { e.preventDefault(); lookup(deviceKey.trim(), mac.trim()); };
  useEffect(() => { if (deviceKey && mac) lookup(deviceKey, mac); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onScan = useCallback(({ key, mac: m }) => {
    const k = key.toUpperCase(), fm = formatMac(m);
    setScanning(false); setDeviceKey(k); setMac(fm);
    if (k && fm) lookup(k, fm);
    else setError("QR kod je očitan, ali nedostaje Device Key ili MAC — dopunite ručno.");
  }, []);

  const pay = async (planId) => {
    setPaying(planId); setError("");
    const origin = window.location.origin;
    try {
      const r = await api.post("/public/checkout", {
        ...creds(), package_id: planId, origin,
        success_url: `${origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${origin}/payment-cancel`,
      });
      window.location.href = r.data.url;
    } catch (err) { setError(errMsg(err, "Plaćanje nije moguće pokrenuti.")); setPaying(null); }
  };

  return (
    <div className="flex min-h-screen flex-col items-center bg-surface px-4 pb-12 pt-6" data-testid="activate-web-page">
      <div className="flex w-full max-w-[520px] flex-col gap-3">
        <Link to="/landing" data-testid="web-back-home" className="flex items-center gap-1.5 self-start py-2 text-sm font-medium text-txt2 hover:text-white"><ArrowLeft size={18} /> Početna</Link>
        <img src="/assets/logo-full-sm.png" alt="BalkanHD Play" className="h-[130px] w-[130px] self-center object-contain" />
        <h1 className="text-2xl font-bold">Aktivirajte plejer</h1>
        <p className="text-xs leading-5 text-txt2">Pratite korake ispod. Ako ste skenirali QR kod sa TV-a, podaci su već upisani — samo kliknite „Proveri uređaj”.</p>
        <ActivationSteps compact />

        <button type="button" onClick={() => setScanning(true)} data-testid="web-scan-qr" className="btn-ghost mt-2 w-full">
          <QrCode size={20} weight="bold" className="text-brand" /> Skeniraj QR kod sa TV-a
        </button>
        <p className="text-center text-[11px] text-muted2">ili unesite podatke ručno</p>
        {scanning && <QrScanner onResult={onScan} onClose={() => setScanning(false)} />}

        <form onSubmit={check} className="flex flex-col gap-3" data-testid="device-lookup-form">
          <label className="mt-2 text-xs font-medium text-txt2" htmlFor="dk">Device Key</label>
          <input id="dk" className="input" value={deviceKey} onChange={(e) => setDeviceKey(e.target.value.toUpperCase())} placeholder="npr. 2XJW9V" autoComplete="off" data-testid="web-device-key" />
          <label className="mt-2 text-xs font-medium text-txt2" htmlFor="mac">MAC adresa</label>
          <input id="mac" className="input" value={mac} onChange={(e) => setMac(formatMac(e.target.value))} maxLength={17} placeholder="npr. 54:40:FE:10:8D:2F" autoComplete="off" data-testid="web-mac" />
          {error && <p className="text-xs font-medium text-err" data-testid="web-error">{error}</p>}
          <button type="submit" disabled={checking} data-testid="web-check" className="btn-primary mt-2">{checking ? <Spinner white /> : "Proveri uređaj"}</button>
        </form>

        {!device && plans.length > 0 && <PricePreview plans={plans} />}

        {device && (
          <div className="mt-2 flex flex-col gap-3 fade-up" data-testid="device-result">
            <div className="flex items-center gap-2 rounded-xl border border-line bg-surface2 p-3">
              <CheckCircle size={22} weight="fill" className="text-ok" />
              <div className="flex-1">
                <p className="text-sm font-bold tracking-wider" data-testid="device-key-mac">{device.device_key} • {device.mac}</p>
                <p className="mt-0.5 text-xs text-txt2" data-testid="device-status-text">{statusText(device)}</p>
              </div>
            </div>
            <PlanPicker device={device} plans={plans} loading={loading} paying={paying} onPay={pay} />
          </div>
        )}

        <div className="mt-6 flex gap-2">
          <ShieldCheck size={16} weight="fill" className="shrink-0 text-muted2" />
          <p className="text-[11px] leading-4 text-muted2">BalkanHD Play je neutralni medija plejer za IPTV/Xtream liste. Aplikacija ne sadrži i ne obezbeđuje kanale, filmove ni serije — sadržaj unosi korisnik.</p>
        </div>
      </div>
      <DownloadCard />
    </div>
  );
}
